import React, { useState } from 'react';
import { TahunAjaran } from '../../types/index.js';
import { Plus, Edit2, Trash2, Search, CheckCircle2 } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';

interface TahunAjaranViewProps {
  tahunAjaranList: TahunAjaran[];
  onAddTahunAjaran: (data: Partial<TahunAjaran>) => Promise<void>;
}

export const TahunAjaranView: React.FC<TahunAjaranViewProps> = ({
  tahunAjaranList,
  onAddTahunAjaran,
}) => {
  const { showAlert } = useDialog();
  const [isAktif, setIsAktif] = useState(false);
  const [tahun, setTahun] = useState('');
  const [deskripsi, setDeskripsi] = useState('');
  const [showModal, setShowModal] = useState(false);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredTahun,
  } = useDataTable<TahunAjaran>({
    data: tahunAjaranList,
    searchFields: ['tahun', 'deskripsi'],
    initialSortBy: 'tahun',
    initialSortDir: 'desc',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tahun) return;
    await onAddTahunAjaran({ tahun, deskripsi, isAktif });
    setShowModal(false);
    setTahun('');
    setDeskripsi('');
    setIsAktif(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Action Header Matching Page 4 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Tahun Ajaran</h2>
          <p className="text-xs text-slate-500">Daftar periode kalender tahun pelajaran sekolah SDMT Ponorogo</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-44"
            />
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah</span>
          </button>
        </div>
      </div>

      {/* Table Matching Page 4 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-4 w-28">Aksi</th>
                <th className="py-2.5 px-4 w-16 text-center">No</th>
                <SortableHeader label="Tahun Ajaran" columnKey="tahun" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="w-48" />
                <SortableHeader label="Deskripsi" columnKey="deskripsi" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Status" columnKey="isAktif" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-center w-32" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTahun.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-400">
                    Tidak ada data tahun ajaran yang cocok
                  </td>
                </tr>
              ) : (
                filteredTahun.map((t, idx) => (
                  <tr key={t.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => showAlert('Fitur edit periode segera hadir', { title: 'Edit' })}
                          className="px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-300 transition flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => showAlert('Fitur hapus periode segera hadir', { title: 'Hapus' })}
                          className="px-2 py-0.5 text-[11px] font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 rounded border border-rose-300 transition flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Del</span>
                        </button>
                      </div>
                    </td>
                    <td className="py-2 px-4 text-slate-500 text-center">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td className="py-2 px-4 font-bold text-sky-900">{t.tahun}</td>
                    <td className="py-2 px-4 text-slate-600">{t.deskripsi}</td>
                    <td className="py-2 px-4 text-center">
                      {t.isAktif ? (
                        <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded flex items-center justify-center gap-1 mx-auto w-max text-[10px]">
                          <CheckCircle2 className="w-3 h-3" /> Aktif
                        </span>
                      ) : (
                        <span className="bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded flex items-center justify-center gap-1 mx-auto w-max text-[10px]">
                          Tidak Aktif
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b">
              Tambah Tahun Ajaran Baru
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Tahun Ajaran</label>
                <input
                  type="text"
                  placeholder="Contoh: 2026/2027"
                  value={tahun}
                  onChange={(e) => setTahun(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Deskripsi</label>
                <input
                  type="text"
                  placeholder="Tahun Pelajaran 2026/2027"
                  value={deskripsi}
                  onChange={(e) => setDeskripsi(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAktifCheckbox"
                  checked={isAktif}
                  onChange={(e) => setIsAktif(e.target.checked)}
                  className="rounded text-sky-600 focus:ring-sky-500"
                />
                <label htmlFor="isAktifCheckbox" className="text-slate-700 font-semibold">
                  Jadikan Tahun Ajaran Aktif
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-3 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md font-semibold"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
